"use client";

import { trackComponent } from "@repo/analytics";
import { getCustomerIdForAnalytics } from "../../utils/analytics-utils";
import {
  authenticationConsentCrossIconInteraction,
  authenticationConsentDrawerComponentId,
  authenticationConsentDrawerInteractionButton,
  authenticationConsentDrawerSectionTitle,
  campaignName,
  ctaGroupCtaButtonClickEventType,
  ctaGroupCtaComponentName,
  ctaGroupFullNewsGroupCustAttribute1,
  ctaGroupPropertyTypePdp,
  ctaGroupSrNumber,
  drawerContentDrawerButtonClickEventType,
  drawerContentDrawerComponentName,
  drawerContentDrawerIconClickEventType,
  gaEventTypeClick,
  journeyTag,
  masterCtaButtonFloatingButtonClickEventType,
  masterCtaButtonFloatingComponentName,
  masterCtaFloatingCustAttribute1,
  productCategory,
  propertyType,
  referenceId,
  selectStockDrawerCustAttribute1,
  sprint,
  subSection,
  tickerStripDefaultSectionTitle,
  tickerStripDisplayBannerButtonClickEventType,
  tickerStripDisplayBannerComponentName,
  tickerStripDisplayBannerIconClickEventType,
  tickerStripFloatingStripCustAttribute1,
  tickerStripSubSectionCrossIcon,
  tickerStripSubSectionOpenDematAccount,
} from "../../pages/News/newsTrackingConstants";

export type AuthenticationConsentDrawerInteraction =
  | typeof authenticationConsentDrawerInteractionButton
  | typeof authenticationConsentCrossIconInteraction;

/**
 * BFSL consent modal — `component_id` → CleverTap `EP_REFERENCE_ID` (see `trackComponent`).
 * Stock news CT DLS: button vs icon (cross) `drawer_content_drawer_*_click`.
 *
 * @param ctaText → data layer `cta_text` / CleverTap `EP_CTA_TEXT`.
 *        Cross close: `authenticationConsentCrossIconCtaText` + `authenticationConsentCrossIconInteraction`.
 */
export async function trackAuthenticationConsentDrawerClick(
  ctaText: string,
  epSubSection: string = "",
  interaction: AuthenticationConsentDrawerInteraction = authenticationConsentDrawerInteractionButton,
): Promise<void> {
  const eventType =
    interaction === authenticationConsentCrossIconInteraction
      ? drawerContentDrawerIconClickEventType
      : drawerContentDrawerButtonClickEventType;

  try {
    const customerId = await getCustomerIdForAnalytics();

    await trackComponent({
      componentName: drawerContentDrawerComponentName,
      componentId: authenticationConsentDrawerComponentId,
      sectionTitle: authenticationConsentDrawerSectionTitle,
      eventType,
      clickType: eventType,
      gaEventType: gaEventTypeClick,
      ctaText,
      productCategory,
      subSection,
      journeyTag,
      cleverTapOverrides: {
        EP_JOURNEY: journeyTag,
        EP_PROPERTY_TYPE: propertyType,
        EP_PRODUCT_NAME: subSection,
        EP_CAMPAIGN: campaignName,
        EP_SPRINT: sprint,
        ...(customerId && { EP_CUSTOMER_ID: customerId }),
        EP_CUST_ATTRIBUTE_1: authenticationConsentDrawerComponentId,
        ...(epSubSection && { EP_SUB_SECTION: epSubSection }),
      },
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      "Failed to track authentication_consent_drawer event",
      error,
    );
  }
}

export async function trackDematCtaGroupEvent(
  headerName: string,
  ctaText: string,
  submissionStatus: string = "",
): Promise<void> {
  try {
    const customerId = await getCustomerIdForAnalytics();

    await trackComponent({
      componentName: ctaGroupCtaComponentName,
      sectionTitle: headerName,
      eventType: ctaGroupCtaButtonClickEventType,
      ctaText,
      productCategory,
      subSection,
      cleverTapOverrides: {
        EP_JOURNEY: journeyTag,
        EP_PROPERTY_TYPE: ctaGroupPropertyTypePdp,
        EP_PRODUCT_NAME: subSection,
        EP_CAMPAIGN: campaignName,
        EP_SPRINT: sprint,
        EP_REFERENCE_ID: referenceId,
        ...(customerId && { EP_CUSTOMER_ID: customerId }),
        EP_SR_NUMBER: ctaGroupSrNumber,
        EP_CUST_ATTRIBUTE_1: ctaGroupFullNewsGroupCustAttribute1,
        ...(submissionStatus && { EP_SUB_SECTION: submissionStatus }),
      },
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to track cta_group_cta event", error);
  }
}

export async function trackTickerStripDisplayBannerButtonClick(
  floatingBannerMainText: string | undefined,
  ctaText: string,
): Promise<void> {
  try {
    const customerId = await getCustomerIdForAnalytics();

    await trackComponent({
      componentName: tickerStripDisplayBannerComponentName,
      sectionTitle: floatingBannerMainText ?? tickerStripDefaultSectionTitle,
      eventType: tickerStripDisplayBannerButtonClickEventType,
      ctaText,
      productCategory,
      subSection,
      cleverTapOverrides: {
        EP_JOURNEY: journeyTag,
        EP_PROPERTY_TYPE: propertyType,
        EP_PRODUCT_NAME: subSection,
        EP_CAMPAIGN: campaignName,
        EP_SPRINT: sprint,
        EP_REFERENCE_ID: referenceId,
        ...(customerId && { EP_CUSTOMER_ID: customerId }),
        EP_CUST_ATTRIBUTE_1: tickerStripFloatingStripCustAttribute1,
        EP_SUB_SECTION: tickerStripSubSectionOpenDematAccount,
      },
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to track ticker_strip_display_banner button", error);
  }
}

export async function trackTickerStripDisplayBannerIconClick(
  floatingBannerMainText: string | undefined,
  ctaText: string,
): Promise<void> {
  try {
    const customerId = await getCustomerIdForAnalytics();

    await trackComponent({
      componentName: tickerStripDisplayBannerComponentName,
      sectionTitle: floatingBannerMainText ?? tickerStripDefaultSectionTitle,
      eventType: tickerStripDisplayBannerIconClickEventType,
      ctaText,
      productCategory,
      subSection,
      cleverTapOverrides: {
        EP_JOURNEY: journeyTag,
        EP_PROPERTY_TYPE: propertyType,
        EP_PRODUCT_NAME: subSection,
        EP_CAMPAIGN: campaignName,
        EP_SPRINT: sprint,
        EP_REFERENCE_ID: referenceId,
        ...(customerId && { EP_CUSTOMER_ID: customerId }),
        EP_SR_NUMBER: ctaGroupSrNumber,
        EP_CUST_ATTRIBUTE_1: tickerStripFloatingStripCustAttribute1,
        EP_SUB_SECTION: tickerStripSubSectionCrossIcon,
      },
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to track ticker_strip_display_banner close", error);
  }
}

export async function trackFloatingFabMasterCtaEvent(
  headerName: string,
  ctaText: string,
  submissionStatus: string = "",
): Promise<void> {
  try {
    const customerId = await getCustomerIdForAnalytics();

    await trackComponent({
      componentName: masterCtaButtonFloatingComponentName,
      sectionTitle: headerName,
      eventType: masterCtaButtonFloatingButtonClickEventType,
      ctaText,
      productCategory,
      subSection,
      cleverTapOverrides: {
        EP_JOURNEY: journeyTag,
        EP_PROPERTY_TYPE: propertyType,
        EP_PRODUCT_NAME: subSection,
        EP_CAMPAIGN: campaignName,
        EP_SPRINT: sprint,
        EP_REFERENCE_ID: referenceId,
        ...(customerId && { EP_CUSTOMER_ID: customerId }),
        EP_CUST_ATTRIBUTE_1: masterCtaFloatingCustAttribute1,
        ...(submissionStatus && { EP_SUB_SECTION: submissionStatus }),
      },
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to track master_cta_button_floating event", error);
  }
}

export async function trackDrawerContentDrawerClick(
  sectionTitle: string,
  ctaText: string,
): Promise<void> {
  try {
    const customerId = await getCustomerIdForAnalytics();

    await trackComponent({
      componentName: drawerContentDrawerComponentName,
      sectionTitle,
      eventType: drawerContentDrawerButtonClickEventType,
      ctaText,
      productCategory,
      subSection,
      cleverTapOverrides: {
        EP_JOURNEY: journeyTag,
        EP_PROPERTY_TYPE: propertyType,
        EP_PRODUCT_NAME: subSection,
        EP_CAMPAIGN: campaignName,
        EP_SPRINT: sprint,
        EP_REFERENCE_ID: referenceId,
        ...(customerId && { EP_CUSTOMER_ID: customerId }),
        EP_CUST_ATTRIBUTE_1: selectStockDrawerCustAttribute1,
      },
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to track drawer_content_drawer event", error);
  }
}
